/*
 * The scope selector — recruiter-app-spec §4.3, "top bar, beside the tenant switcher".
 *
 * Placed there rather than on each screen because §4.2 makes it a property of the *session*, not
 * of the page: a manager who switches to their own work expects Home, Jobs and Candidates to
 * follow, and a per-screen control would let three screens disagree about whose work is on show.
 *
 * **It disappears when there is nothing to choose.** A recruiter holds one scope, so rendering a
 * one-option dropdown for them would be a control that does nothing — and a control that does
 * nothing still gets clicked, and still has to be explained. What matters is that the *screens*
 * are scoped, and they are whether or not this is visible.
 *
 * Labelled rather than iconic: "Mine" and "The agency" say what the numbers below will mean, and
 * this is the one control on the page that changes every other number on it.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@indovation/i18n/react';
import type { MessageKey } from '@indovation/i18n';
import { useScope } from './scope.js';
import type { QueryScope } from './scope.js';
import { Select } from '@indovation/design-system';

export default function ScopeSelector() {
  const { t } = useI18n();
  const { scope, available, setScope } = useScope();
  const queryClient = useQueryClient();

  if (available.length < 2) {
    return null;
  }

  return (
    <label className="tf-scope">
      <span className="tf-scope__label">{t('scope.label')}</span>
      <Select
        value={scope}
        onChange={(event) => {
          setScope(event.currentTarget.value as QueryScope);
          /*
           * Everything, not just the screen in front of us.
           *
           * Scope is part of what every cached answer *means*, and the query keys below include
           * it — so React Query would treat the new scope as a cache miss and refetch anyway. The
           * invalidation is for the answers already held under the *old* scope: without it, going
           * back to the previous setting would render a stale count from before whatever has
           * happened since, which is exactly the moment a dashboard stops being trusted.
           */
          void queryClient.invalidateQueries();
        }}
      >
        {available.map((option) => (
          <option key={option} value={option}>
            {t(`scope.option.${option}` as MessageKey)}
          </option>
        ))}
      </Select>
    </label>
  );
}
